import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Badge, Input, Select, cn } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import BlockEditor from './BlockEditor';
import {
  FUNNEL_WRITE_ROLES,
  BLOCK_TYPES,
  STEP_TYPES,
  FUNNEL_STATUS_OPTIONS,
  getDefaultBlockContent,
  getFunnelLiveUrl,
  summarizeBlockContent,
  slugify,
} from '../../lib/funnelUtils';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';
import * as FiIcons from 'react-icons/fi';

const {
  FiSettings, FiExternalLink, FiDownload, FiUpload, FiPlus, FiTrash2,
  FiEye, FiEyeOff, FiZap, FiSave, FiLayers, FiInbox,
} = FiIcons;

export default function FunnelBuilder() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const saveTimerRef = useRef(null);
  const { users: directoryUsers } = useUsersDirectory();
  const users = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);

  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [dragStepId, setDragStepId] = useState(null);
  const [dragBlockId, setDragBlockId] = useState(null);

  const [addStepOpen, setAddStepOpen] = useState(false);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [deleteStepTarget, setDeleteStepTarget] = useState(null);
  const [deleteBlockTarget, setDeleteBlockTarget] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [newStep, setNewStep] = useState({ step_type: 'custom', title: '', slug: '' });

  const canWrite = FUNNEL_WRITE_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const funnelData = await api.funnels.getById(id);
      if (!funnelData) {
        addToast({ type: 'error', title: 'Funnel not found' });
        navigate('/funnels');
        return;
      }
      setFunnel(funnelData);
      setSelectedStepId((prev) => prev || funnelData.steps?.[0]?.id || null);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load funnel', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id, addToast, navigate]);

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedStep = funnel?.steps?.find((s) => s.id === selectedStepId);
  const blocks = selectedStep?.blocks ?? [];
  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  const debouncedBlockSave = useCallback((blockId, content) => {
    if (!canWrite) return;
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.funnelBlocks.update(blockId, { content });
        setSaveStatus('saved');
      } catch (err) {
        setSaveStatus('error');
        addToast({ type: 'error', title: 'Save failed', message: err.message });
      }
    }, 500);
  }, [canWrite, addToast]);

  const handleBlockContentChange = (content) => {
    if (!selectedBlock) return;
    setFunnel((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === selectedStepId
          ? {
              ...step,
              blocks: step.blocks.map((b) =>
                b.id === selectedBlock.id ? { ...b, content } : b,
              ),
            }
          : step,
      ),
    }));
    debouncedBlockSave(selectedBlock.id, content);
  };

  const handleFunnelNameChange = (name) => {
    setFunnel((prev) => ({ ...prev, name }));
  };

  const saveFunnelName = async () => {
    if (!canWrite || !funnel) return;
    setSaveStatus('saving');
    try {
      await api.funnels.update(id, { name: funnel.name });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      addToast({ type: 'error', title: 'Save failed', message: err.message });
    }
  };

  const handleStatusChange = async (status) => {
    if (!canWrite) return;
    try {
      await api.funnels.update(id, { status });
      setFunnel((prev) => ({ ...prev, status }));
      addToast({ type: 'success', title: `Status: ${status}` });
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    }
  };

  const handleStepReorder = async (targetStepId) => {
    if (!canWrite || !dragStepId || dragStepId === targetStepId) return;
    const steps = [...(funnel.steps ?? [])];
    const fromIdx = steps.findIndex((s) => s.id === dragStepId);
    const toIdx = steps.findIndex((s) => s.id === targetStepId);
    if (fromIdx < 0 || toIdx < 0) return;

    const [moved] = steps.splice(fromIdx, 1);
    steps.splice(toIdx, 0, moved);
    const orderedIds = steps.map((s) => s.id);

    setFunnel((prev) => ({
      ...prev,
      steps: steps.map((s, i) => ({ ...s, step_number: i + 1 })),
    }));

    try {
      await api.funnelSteps.reorder(id, orderedIds);
    } catch (err) {
      addToast({ type: 'error', title: 'Reorder failed', message: err.message });
      load();
    }
    setDragStepId(null);
  };

  const handleBlockReorder = async (targetBlockId) => {
    if (!canWrite || !dragBlockId || !selectedStepId || dragBlockId === targetBlockId) return;
    const stepBlocks = [...blocks];
    const fromIdx = stepBlocks.findIndex((b) => b.id === dragBlockId);
    const toIdx = stepBlocks.findIndex((b) => b.id === targetBlockId);
    if (fromIdx < 0 || toIdx < 0) return;

    const [moved] = stepBlocks.splice(fromIdx, 1);
    stepBlocks.splice(toIdx, 0, moved);
    const orderedIds = stepBlocks.map((b) => b.id);

    setFunnel((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === selectedStepId
          ? { ...step, blocks: stepBlocks.map((b, i) => ({ ...b, position: i })) }
          : step,
      ),
    }));

    try {
      await api.funnelBlocks.reorder(selectedStepId, orderedIds);
    } catch (err) {
      addToast({ type: 'error', title: 'Reorder failed', message: err.message });
      load();
    }
    setDragBlockId(null);
  };

  const handleAddStep = async () => {
    if (!canWrite) return;
    setActionLoading(true);
    try {
      const slug = newStep.slug || slugify(newStep.title);
      const step = await api.funnelSteps.create({
        funnel_id: id,
        agency_id: funnel.agency_id,
        step_type: newStep.step_type,
        title: newStep.title,
        slug,
      });
      await load();
      setSelectedStepId(step.id);
      setAddStepOpen(false);
      setNewStep({ step_type: 'custom', title: '', slug: '' });
      addToast({ type: 'success', title: 'Step added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Add step failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteStep = async () => {
    if (!deleteStepTarget || (funnel?.steps?.length ?? 0) <= 1) return;
    setActionLoading(true);
    try {
      await api.funnelSteps.delete(deleteStepTarget.id);
      addToast({ type: 'success', title: 'Step deleted' });
      setDeleteStepTarget(null);
      setSelectedBlockId(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddBlock = async (blockType) => {
    if (!canWrite || !selectedStepId) return;
    setActionLoading(true);
    try {
      const block = await api.funnelBlocks.create({
        step_id: selectedStepId,
        agency_id: funnel.agency_id,
        block_type: blockType,
        content: getDefaultBlockContent(blockType),
      });
      await load();
      setSelectedBlockId(block.id);
      setAddBlockOpen(false);
      addToast({ type: 'success', title: 'Block added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Add block failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBlock = async () => {
    if (!deleteBlockTarget) return;
    setActionLoading(true);
    try {
      await api.funnelBlocks.delete(deleteBlockTarget.id);
      if (selectedBlockId === deleteBlockTarget.id) setSelectedBlockId(null);
      setDeleteBlockTarget(null);
      await load();
      addToast({ type: 'success', title: 'Block deleted' });
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleBlockVisibility = async (block) => {
    if (!canWrite) return;
    try {
      await api.funnelBlocks.update(block.id, { is_visible: !block.is_visible });
      setFunnel((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.id === selectedStepId
            ? {
                ...step,
                blocks: step.blocks.map((b) =>
                  b.id === block.id ? { ...b, is_visible: !b.is_visible } : b,
                ),
              }
            : step,
        ),
      }));
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    }
  };

  const handleExport = async () => {
    try {
      const json = await api.funnels.exportJson(id);
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${funnel.slug || 'funnel'}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      addToast({ type: 'success', title: 'Export downloaded' });
    } catch (err) {
      addToast({ type: 'error', title: 'Export failed', message: err.message });
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !canWrite) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await api.funnels.replaceFromJson(id, json);
      addToast({ type: 'success', title: 'Funnel config imported' });
      setSelectedBlockId(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Import failed', message: err.message });
    }
    e.target.value = '';
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setActionLoading(true);
    try {
      await api.funnelTemplates.saveAsTemplate(id, templateName.trim(), templateDesc.trim());
      addToast({ type: 'success', title: 'Saved as template' });
      setTemplateModalOpen(false);
      setTemplateName('');
      setTemplateDesc('');
    } catch (err) {
      addToast({ type: 'error', title: 'Save template failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[12rem] gap-3">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden bg-gray-100">
      {/* Top bar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] flex items-center gap-2">
          <input
            type="text"
            value={funnel?.name ?? ''}
            onChange={(e) => handleFunnelNameChange(e.target.value)}
            onBlur={saveFunnelName}
            disabled={!canWrite}
            className="text-lg font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-primary focus:outline-none flex-1 min-w-0"
          />
          <span className={cn(
            'text-xs',
            saveStatus === 'saving' && 'text-amber-600',
            saveStatus === 'saved' && 'text-gray-400',
            saveStatus === 'error' && 'text-red-600',
          )}>
            {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'error' ? 'Save failed' : 'Saved'}
          </span>
        </div>

        {canWrite && (
          <Select
            value={funnel?.status ?? 'draft'}
            onChange={(e) => handleStatusChange(e.target.value)}
            options={FUNNEL_STATUS_OPTIONS}
            className="w-32"
          />
        )}
        {!canWrite && (
          <Badge variant={funnel?.status === 'active' ? 'success' : 'default'}>{funnel?.status}</Badge>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {funnel?.status === 'active' && (
            <Button variant="outline" size="sm" onClick={() => window.open(getFunnelLiveUrl(funnel.slug), '_blank')}>
              <SafeIcon icon={FiExternalLink} className="mr-1" />
              Preview
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/funnels/${id}/settings`)}>
            <SafeIcon icon={FiSettings} className="mr-1" />
            Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/funnels/${id}/submissions`)}>
            <SafeIcon icon={FiInbox} className="mr-1" />
            Submissions
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <SafeIcon icon={FiDownload} className="mr-1" />
            Export
          </Button>
          {canWrite && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <SafeIcon icon={FiUpload} className="mr-1" />
                Import
              </Button>
              <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
              <Button variant="outline" size="sm" onClick={() => setTemplateModalOpen(true)}>
                <SafeIcon icon={FiLayers} className="mr-1" />
                Save Template
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Three panels */}
      <div className="flex-1 flex flex-row min-h-0 min-w-0 overflow-hidden">
        {/* Left — Step navigator */}
        <aside className="w-[200px] shrink-0 min-h-0 bg-sidebar text-white border-r border-gray-800 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-800 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Steps</span>
            {canWrite && (
              <button type="button" onClick={() => setAddStepOpen(true)} className="p-1 rounded hover:bg-sidebarHover text-primary">
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {(funnel?.steps ?? []).map((step) => (
              <div
                key={step.id}
                draggable={canWrite}
                onDragStart={() => setDragStepId(step.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleStepReorder(step.id)}
                onClick={() => { setSelectedStepId(step.id); setSelectedBlockId(null); }}
                className={cn(
                  'rounded-md px-2 py-2 cursor-pointer text-sm transition-colors',
                  selectedStepId === step.id ? 'bg-primary text-white' : 'hover:bg-sidebarHover text-gray-300',
                )}
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono opacity-70">{step.step_number}</span>
                  {step.is_entry_point && (
                    <SafeIcon icon={FiZap} className="w-3 h-3 text-amber-400" title="Entry point" />
                  )}
                </div>
                <p className="font-medium truncate">{step.title}</p>
                <span className="text-[10px] uppercase opacity-60">{step.step_type}</span>
              </div>
            ))}
          </div>
          {canWrite && selectedStep && (funnel?.steps?.length ?? 0) > 1 && (
            <div className="p-2 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setDeleteStepTarget(selectedStep)}
                className="w-full text-xs text-red-400 hover:text-red-300 py-1"
              >
                Delete step
              </button>
            </div>
          )}
        </aside>

        {/* Center — Block canvas */}
        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">
              {selectedStep ? `Step ${selectedStep.step_number}: ${selectedStep.title}` : 'Select a step'}
            </h2>
            {canWrite && selectedStep && (
              <Button size="sm" onClick={() => setAddBlockOpen(true)}>
                <SafeIcon icon={FiPlus} className="mr-1" />
                Add Block
              </Button>
            )}
          </div>

          {!selectedStep ? (
            <EmptyState title="No step selected" description="Choose a step from the left panel." />
          ) : blocks.length === 0 ? (
            <EmptyState
              title="No blocks yet"
              description="Add your first content block to this step."
              actionLabel={canWrite ? 'Add Block' : undefined}
              onAction={canWrite ? () => setAddBlockOpen(true) : undefined}
            />
          ) : (
            <div className="space-y-3 max-w-2xl mx-auto">
              {blocks.map((block) => (
                <div
                  key={block.id}
                  draggable={canWrite}
                  onDragStart={() => setDragBlockId(block.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleBlockReorder(block.id)}
                  onClick={() => setSelectedBlockId(block.id)}
                  className={cn(
                    'rounded-lg border-2 bg-white p-4 cursor-pointer transition-all',
                    selectedBlockId === block.id ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300',
                    !block.is_visible && 'opacity-50',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-xs font-semibold text-primary uppercase">{block.block_type}</span>
                      <p className="text-sm text-gray-600 mt-1">{summarizeBlockContent(block)}</p>
                    </div>
                    <div className="flex gap-1">
                      {canWrite && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleBlockVisibility(block); }}
                            className="p-1.5 rounded text-gray-400 hover:text-primary"
                            title={block.is_visible ? 'Hide' : 'Show'}
                          >
                            <SafeIcon icon={block.is_visible ? FiEye : FiEyeOff} className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDeleteBlockTarget(block); }}
                            className="p-1.5 rounded text-gray-400 hover:text-red-600"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Right — Block editor */}
        <aside className="w-[320px] shrink-0 min-h-0 bg-white border-l border-gray-200 overflow-hidden flex flex-col">
          <BlockEditor
            block={selectedBlock}
            steps={funnel?.steps}
            users={users}
            onChange={handleBlockContentChange}
            disabled={!canWrite}
          />
        </aside>
      </div>

      {/* Add step modal */}
      <Modal isOpen={addStepOpen} onClose={() => setAddStepOpen(false)} title="Add step">
        <div className="space-y-4 mt-2">
          <Select
            label="Step type"
            value={newStep.step_type}
            onChange={(e) => setNewStep((s) => ({ ...s, step_type: e.target.value }))}
            options={STEP_TYPES}
          />
          <Input
            label="Title"
            value={newStep.title}
            onChange={(e) => setNewStep((s) => ({ ...s, title: e.target.value, slug: slugify(e.target.value) }))}
          />
          <Input
            label="Slug"
            value={newStep.slug}
            onChange={(e) => setNewStep((s) => ({ ...s, slug: slugify(e.target.value) }))}
          />
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setAddStepOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAddStep} isLoading={actionLoading} disabled={!newStep.title.trim()}>Add Step</Button>
          </div>
        </div>
      </Modal>

      {/* Add block modal */}
      <Modal isOpen={addBlockOpen} onClose={() => setAddBlockOpen(false)} title="Add block">
        <div className="grid grid-cols-2 gap-3 mt-2">
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.value}
              type="button"
              onClick={() => handleAddBlock(bt.value)}
              disabled={actionLoading}
              className="text-left rounded-lg border border-gray-200 p-3 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <p className="font-medium text-sm text-gray-900">{bt.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{bt.description}</p>
            </button>
          ))}
        </div>
      </Modal>

      <Modal isOpen={Boolean(deleteStepTarget)} onClose={() => setDeleteStepTarget(null)} title="Delete step?">
        <p className="text-sm text-gray-600 mt-2">
          Delete step &quot;{deleteStepTarget?.title}&quot; and all its blocks?
        </p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" size="sm" onClick={() => setDeleteStepTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDeleteStep} isLoading={actionLoading}>Delete</Button>
        </div>
      </Modal>

      <Modal isOpen={Boolean(deleteBlockTarget)} onClose={() => setDeleteBlockTarget(null)} title="Delete block?">
        <p className="text-sm text-gray-600 mt-2">Remove this {deleteBlockTarget?.block_type} block?</p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" size="sm" onClick={() => setDeleteBlockTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDeleteBlock} isLoading={actionLoading}>Delete</Button>
        </div>
      </Modal>

      <Modal isOpen={templateModalOpen} onClose={() => setTemplateModalOpen(false)} title="Save as template">
        <div className="space-y-4 mt-2">
          <Input label="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
          <Input label="Description" value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)} />
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveTemplate} isLoading={actionLoading} disabled={!templateName.trim()}>
              <SafeIcon icon={FiSave} className="mr-1" />
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
